import { memo, useLayoutEffect, useRef, useState, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** Must match `$campaign-flow-actions-clearance` in CampaignFlow.scss */
const ACTIONS_CLEARANCE_PX = 72;

interface CampaignFlowActionsProps {
  onBack: () => void;
  children: ReactNode;
  backDisabled?: boolean;
  wide?: boolean;
  sticky?: boolean;
  backLabel?: string;
  note?: string;
  className?: string;
}

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let current = node?.parentElement ?? null;
  while (current) {
    const { overflow, overflowY } = getComputedStyle(current);
    if (/(auto|scroll|overlay)/.test(`${overflow}${overflowY}`)) {
      return current;
    }
    current = current.parentElement;
  }
  return document.documentElement;
};

const CampaignFlowActions: FC<CampaignFlowActionsProps> = ({
  onBack,
  children,
  backDisabled = false,
  wide = false,
  sticky = false,
  backLabel,
  note,
  className = "",
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const dockedRef = useRef(false);
  const [docked, setDocked] = useState(false);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (sticky || !el) {
      return;
    }

    const scrollParent = getScrollParent(el);
    if (!scrollParent) {
      return;
    }

    let settleId = 0;

    const update = () => {
      const { scrollHeight, clientHeight } = scrollParent;
      const next = dockedRef.current
        ? // Body has clearance padding while docked — ignore it when deciding to undock.
          scrollHeight - ACTIONS_CLEARANCE_PX > clientHeight + 1
        : scrollHeight > clientHeight + 1;

      if (next !== dockedRef.current) {
        dockedRef.current = next;
        setDocked(next);
        // Re-measure after dock class / body clearance padding is applied.
        cancelAnimationFrame(settleId);
        settleId = requestAnimationFrame(update);
      }
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(scrollParent);
    const flowRoot = el.closest(".campaign-flow");
    if (flowRoot) {
      observer.observe(flowRoot);
    }
    window.addEventListener("resize", update);

    return () => {
      cancelAnimationFrame(settleId);
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [sticky]);

  const rootClassName = [
    sticky ? "campaign-flow__footer" : "campaign-flow__actions",
    wide && !sticky ? "campaign-flow__actions--wide" : "",
    docked && !sticky ? "campaign-flow__actions--docked" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={rootRef} className={rootClassName}>
      <button
        type="button"
        className="button button--outline campaign-flow__action"
        onClick={onBack}
        disabled={backDisabled}
      >
        {backLabel ?? t("campaignFlow.common.back")}
      </button>
      {note && <span className="campaign-flow__actions-note">{note}</span>}
      <div className="campaign-flow__actions-end">{children}</div>
    </div>
  );
};

export default memo(CampaignFlowActions);
